import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  Modal,
  Button,
  Table,
  Form,
  Row,
  Col,
  Spinner,
  Alert,
} from "react-bootstrap";
import { AppDispatch } from "../../../store";
import { Company } from "../../../features/companies/companiesService";
import {
  getCompanyUsers,
  addUsersToCompany,
  removeUsersFromCompany,
} from "../../../features/companies/companiesSlice";
import { getUsers } from "../../../features/users/usersSlice";
import { User as UserType } from "../../../features/users/usersSlice";

interface CompanyUsersModalProps {
  show: boolean;
  onHide: () => void;
  company: Company;
}

// Şirket kullanıcıları yanıt tipi
interface CompanyUsersResponse {
  companyId: number;
  users: UserType[];
}

const CompanyUsersModal: React.FC<CompanyUsersModalProps> = ({
  show,
  onHide,
  company,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  const [companyUsers, setCompanyUsers] = useState<UserType[]>([]);
  const [availableUsers, setAvailableUsers] = useState<UserType[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [userRole, setUserRole] = useState<string>("member");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Şirket kullanıcılarını ve mevcut kullanıcıları yükle
  const loadUsers = async () => {
    if (!company) return;

    setIsLoading(true);
    setError(null);

    try {
      // Şirket kullanıcıları
      const response = (await dispatch(
        getCompanyUsers(company.id)
      ).unwrap()) as CompanyUsersResponse;
      setCompanyUsers(response.users); // response.users kullanılıyor

      // Tüm kullanıcılar
      const allUsersResponse = await dispatch(getUsers({})).unwrap();
      // API response yapısını kontrol et ve uygun şekilde kullan
      const allUsers = Array.isArray(allUsersResponse)
        ? allUsersResponse
        : allUsersResponse.users || [];

      // Şirkete ait olmayan kullanıcıları filtrele
      const companyUserIds = response.users.map((user: UserType) => user.id); // response.users.map kullanılıyor
      setAvailableUsers(
        allUsers.filter((user: UserType) => !companyUserIds.includes(user.id))
      );
    } catch (err) {
      setError("Kullanıcılar yüklenirken bir hata oluştu.");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (show && company) {
      loadUsers();
    }
  }, [show, company]);

  // Seçilen kullanıcıları yönet
  const handleUserSelect = (userId: number, isSelected: boolean) => {
    if (isSelected) {
      setSelectedUserIds((prev) => [...prev, userId]);
    } else {
      setSelectedUserIds((prev) => prev.filter((id) => id !== userId));
    }
  };

  // Kullanıcı rolünü değiştir
  const handleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setUserRole(e.target.value);
  };

  // Kullanıcı ekle
  const handleAddUsers = async () => {
    if (!selectedUserIds.length) {
      setError("Lütfen en az bir kullanıcı seçin.");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await dispatch(
        addUsersToCompany({
          companyId: company.id,
          userRelation: {
            user_ids: selectedUserIds,
            role: userRole,
          },
        })
      ).unwrap();

      // Seçimleri sıfırla
      setSelectedUserIds([]);
      setUserRole("member");

      // Kullanıcı listesini yenile
      await loadUsers();
    } catch (err) {
      setError("Kullanıcılar eklenirken bir hata oluştu.");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Kullanıcıyı kaldır
  const handleRemoveUser = async (userId: number) => {
    if (
      // eslint-disable-next-line no-restricted-globals
      !confirm("Bu kullanıcıyı şirketten kaldırmak istediğinize emin misiniz?")
    ) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await dispatch(
        removeUsersFromCompany({
          companyId: company.id,
          userRelation: {
            user_ids: [userId],
          },
        })
      ).unwrap();

      // Kullanıcı listesini yenile
      await loadUsers();
    } catch (err) {
      setError("Kullanıcı kaldırılırken bir hata oluştu.");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Şirket Kullanıcıları: {company?.company_name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        <h5 className="mb-3">Kullanıcı Ekle</h5>
        <Row className="mb-4">
          <Col md={8}>
            <Form.Group>
              <Form.Label>Kullanıcı Seç</Form.Label>
              <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                {availableUsers.length === 0 ? (
                  <Alert variant="info">
                    Eklenebilecek kullanıcı bulunamadı.
                  </Alert>
                ) : (
                  availableUsers.map((user) => (
                    <Form.Check
                      key={user.id}
                      type="checkbox"
                      id={`user-${user.id}`}
                      label={`${user.full_name} (${user.email})`}
                      checked={selectedUserIds.includes(user.id)}
                      onChange={(e) =>
                        handleUserSelect(user.id, e.target.checked)
                      }
                    />
                  ))
                )}
              </div>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Rol</Form.Label>
              <Form.Select value={userRole} onChange={handleRoleChange}>
                <option value="member">Üye</option>
                <option value="manager">Yönetici</option>
                <option value="admin">Admin</option>
              </Form.Select>
            </Form.Group>
            <div className="mt-3">
              <Button
                variant="success"
                onClick={handleAddUsers}
                disabled={isLoading || selectedUserIds.length === 0}
              >
                {isLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-1" />
                    Ekleniyor...
                  </>
                ) : (
                  "Kullanıcıları Ekle"
                )}
              </Button>
            </div>
          </Col>
        </Row>

        <h5 className="mt-4 mb-3">Mevcut Kullanıcılar</h5>
        {isLoading ? (
          <div className="text-center py-3">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : companyUsers.length === 0 ? (
          <Alert variant="info">
            Bu şirkete ait kullanıcı bulunmamaktadır.
          </Alert>
        ) : (
          <Table responsive hover>
            <thead>
              <tr>
                <th>ID</th>
                <th>Ad Soyad</th>
                <th>E-posta</th>
                <th>Rol</th>
                <th>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {companyUsers.map((user) => (
                <tr key={user.id}>
                  <td>{user.id}</td>
                  <td>{user.full_name}</td>
                  <td>{user.email}</td>
                  <td>
                    {user.companies.find((c) => c.company_id === company.id)
                      ?.role || "Kullanıcı"}
                  </td>
                  <td>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleRemoveUser(user.id)}
                      disabled={isLoading}
                    >
                      Kaldır
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Kapat
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CompanyUsersModal;
